// Strings for release 7 (0029's frontend, spec §12.C): the platform
// governance UI — Zencera's own screens to manage the six wellbeing areas,
// the student-facing tiles, the answer-scale templates and the platform's
// own follow-up question bank. Finnish and Swedish are drafts pending
// native-speaker review, same as every other release.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };

export const r7En = {
  // Admin nav + page shell (web/src/pages/admin/ContentPage.tsx).
  'r7.cg.nav': 'Content',
  'r7.cg.title': 'Platform content',
  'r7.cg.hint':
    'The platform-managed building blocks behind the student check-in: the six wellbeing areas, the tiles students tap, the answer scales questions are written against, and Zencera’s own question bank.',
  'r7.cg.tabAreas': 'Areas',
  'r7.cg.tabTiles': 'Tiles',
  'r7.cg.tabScales': 'Answer scales',
  'r7.cg.tabQuestions': 'Platform questions',

  'r7.cg.statusActive': 'Active',
  'r7.cg.statusRetired': 'Retired',

  // Areas tab.
  'r7.cg.areaListTitle': 'Wellbeing areas',
  'r7.cg.areaHint':
    'The six SWIPE research areas every follow-up question is scored against (spec §14.1). Adding one takes effect in future check-ins only; retiring keeps its history intact.',
  'r7.cg.areaNewTitle': 'Add an area',
  'r7.cg.areaCode': 'Code',
  'r7.cg.areaCodeHint': 'Lowercase letters, digits and underscores, e.g. feel.',
  'r7.cg.areaSort': 'Sort order',
  'r7.cg.areaSortHint': 'Lower numbers show first. Leave blank to add it at the end.',
  'r7.cg.areaAdd': 'Add area',
  'r7.cg.areaAdded': 'Added.',
  'r7.cg.areaEmpty': 'No areas yet.',
  'r7.cg.areaRetire': 'Retire',
  'r7.cg.areaRetireTitle': 'Retire this area?',
  'r7.cg.areaRetireBody':
    'No new question can target it, and it drops off future check-ins. Past answers keep their meaning — retiring never deletes them.',
  'r7.cg.areaRetiredToast': 'Retired.',

  // Tiles tab.
  'r7.cg.tileListTitle': 'Student-facing tiles',
  'r7.cg.tileHint':
    'What a student actually taps on the check-in (spec §14.2) — a layer above the six areas. A tile can link to more than one area; a tap credits every linked area at full weight, and one linked area is marked primary, the one its follow-up questions are drawn from.',
  'r7.cg.tileNewTitle': 'Add a tile',
  'r7.cg.tileCode': 'Code',
  'r7.cg.tileCodeHint': 'Lowercase letters, digits and underscores, e.g. friends.',
  'r7.cg.tileSort': 'Sort order',
  'r7.cg.tileAdd': 'Add tile',
  'r7.cg.tileAdded': 'Added.',
  'r7.cg.tileEmpty': 'No tiles yet.',
  'r7.cg.tileRetire': 'Retire',
  'r7.cg.tileRetireTitle': 'Retire this tile?',
  'r7.cg.tileRetireBody': 'Students stop seeing it on the check-in. Past answers keep their meaning — retiring never deletes them.',
  'r7.cg.tileRetiredToast': 'Retired.',
  'r7.cg.tileAreasLabel': 'Linked areas',
  'r7.cg.tilePrimaryBadge': 'Primary',
  'r7.cg.tileUnlinkAria': (area: string) => `Unlink ${area}`,
  'r7.cg.tileUnlinked': 'Unlinked.',
  'r7.cg.tileLinkTitle': 'Link an area',
  'r7.cg.tileAreaPick': 'Area',
  'r7.cg.tilePrimaryCheck': 'Primary area — its question pool is what this tile draws from',
  'r7.cg.tileLink': 'Link',
  'r7.cg.tileLinked': 'Linked.',

  // Scales tab.
  'r7.cg.scaleListTitle': 'Answer scale templates',
  'r7.cg.scaleHint':
    'The 3-option answer scales a question is written against (spec §14.9) — low, mid, high, each with its own emoji and label.',
  'r7.cg.scaleNewTitle': 'Add a scale template',
  'r7.cg.scaleCode': 'Code',
  'r7.cg.scaleCodeHint': 'Lowercase letters, digits and underscores, e.g. frequency.',
  'r7.cg.scaleOptionLow': 'Low option',
  'r7.cg.scaleOptionMid': 'Mid option',
  'r7.cg.scaleOptionHigh': 'High option',
  'r7.cg.scaleEmoji': 'Emoji',
  'r7.cg.scaleOptionLabel': 'Label',
  'r7.cg.scaleAdd': 'Add scale',
  'r7.cg.scaleAdded': 'Added.',
  'r7.cg.scaleEmpty': 'No answer scales yet.',
  'r7.cg.scaleRetire': 'Retire',
  'r7.cg.scaleRetireTitle': 'Retire this scale?',
  'r7.cg.scaleRetireBody': 'No new question can be written against it. Questions already using it keep working as before.',
  'r7.cg.scaleRetiredToast': 'Retired.',

  // Platform questions tab.
  'r7.cg.qListTitle': 'Platform question bank',
  'r7.cg.qHint':
    'Zencera-authored follow-up questions, auto-approved and shown to every school unless that school has written its own for the same area.',
  'r7.cg.qNewTitle': 'Add a platform question',
  'r7.cg.qFilterLabel': 'Area',
  'r7.cg.qFilterAll': 'All areas',
  'r7.cg.qLens': 'Lens',
  'r7.cg.qLensThink': 'Think',
  'r7.cg.qLensAct': 'Act',
  'r7.cg.qLensReflect': 'Reflect',
  'r7.cg.qAxis': 'Axis',
  'r7.cg.qAxisThriving': 'Thriving',
  'r7.cg.qAxisStrain': 'Strain',
  'r7.cg.qAdd': 'Add question',
  'r7.cg.qAdded': 'Added.',
  'r7.cg.qEmpty': 'No platform questions yet for this area.',

  'err.bad_code': 'Use lowercase letters, digits and underscores, starting with a letter.',
  'err.duplicate': 'That code is already in use.',
  'err.bad_lens': 'Choose Think, Act or Reflect.',
  'err.bad_axis': 'Choose Thriving or Strain.',
  'err.unknown_tile': 'Unknown or retired tile.',
  'err.unknown_link': 'That tile is not linked to that area.',
  'err.last_area': 'A tile needs at least one linked area.',
  'err.bad_options': 'Give each of the three options an emoji and a label.',
};

export type R7Strings = Shape<typeof r7En>;

export const r7Fi: R7Strings = {
  'r7.cg.nav': 'Sisältö',
  'r7.cg.title': 'Alustan sisältö',
  'r7.cg.hint':
    'Oppilaan kyselyn taustalla olevat alustan hallinnoimat palikat: kuusi hyvinvoinnin osa-aluetta, oppilaan napauttamat laatat, kysymysten vastausasteikot ja Zenceran oma kysymyspankki.',
  'r7.cg.tabAreas': 'Osa-alueet',
  'r7.cg.tabTiles': 'Laatat',
  'r7.cg.tabScales': 'Vastausasteikot',
  'r7.cg.tabQuestions': 'Alustan kysymykset',

  'r7.cg.statusActive': 'Käytössä',
  'r7.cg.statusRetired': 'Poistettu käytöstä',

  'r7.cg.areaListTitle': 'Hyvinvoinnin osa-alueet',
  'r7.cg.areaHint':
    'Kuusi SWIPE-mallin osa-aluetta, joita vasten jokainen jatkokysymys pisteytetään (spec §14.1). Lisäys vaikuttaa vain tuleviin kyselyihin; poistaminen käytöstä säilyttää historian.',
  'r7.cg.areaNewTitle': 'Lisää osa-alue',
  'r7.cg.areaCode': 'Koodi',
  'r7.cg.areaCodeHint': 'Pienet kirjaimet, numerot ja alaviivat, esim. feel.',
  'r7.cg.areaSort': 'Järjestysnumero',
  'r7.cg.areaSortHint': 'Pienempi numero näkyy ensin. Jätä tyhjäksi lisätäksesi listan loppuun.',
  'r7.cg.areaAdd': 'Lisää osa-alue',
  'r7.cg.areaAdded': 'Lisätty.',
  'r7.cg.areaEmpty': 'Ei osa-alueita vielä.',
  'r7.cg.areaRetire': 'Poista käytöstä',
  'r7.cg.areaRetireTitle': 'Poistetaanko tämä osa-alue käytöstä?',
  'r7.cg.areaRetireBody':
    'Sille ei voi enää kirjoittaa uutta kysymystä, ja se jää pois tulevista kyselyistä. Aiemmat vastaukset säilyttävät merkityksensä — käytöstä poistaminen ei koskaan poista niitä.',
  'r7.cg.areaRetiredToast': 'Poistettu käytöstä.',

  'r7.cg.tileListTitle': 'Oppilaan laatat',
  'r7.cg.tileHint':
    'Se, mitä oppilas oikeasti napauttaa kyselyssä (spec §14.2) — kuuden osa-alueen päällä oleva kerros. Laatta voi liittyä useampaan osa-alueeseen; napautus hyvittää kaikki liitetyt osa-alueet täydellä painolla, ja yksi liitetyistä on merkitty ensisijaiseksi — siitä laatta ammentaa jatkokysymyksensä.',
  'r7.cg.tileNewTitle': 'Lisää laatta',
  'r7.cg.tileCode': 'Koodi',
  'r7.cg.tileCodeHint': 'Pienet kirjaimet, numerot ja alaviivat, esim. friends.',
  'r7.cg.tileSort': 'Järjestysnumero',
  'r7.cg.tileAdd': 'Lisää laatta',
  'r7.cg.tileAdded': 'Lisätty.',
  'r7.cg.tileEmpty': 'Ei laattoja vielä.',
  'r7.cg.tileRetire': 'Poista käytöstä',
  'r7.cg.tileRetireTitle': 'Poistetaanko tämä laatta käytöstä?',
  'r7.cg.tileRetireBody':
    'Oppilaat lakkaavat näkemästä sitä kyselyssä. Aiemmat vastaukset säilyttävät merkityksensä — käytöstä poistaminen ei koskaan poista niitä.',
  'r7.cg.tileRetiredToast': 'Poistettu käytöstä.',
  'r7.cg.tileAreasLabel': 'Liitetyt osa-alueet',
  'r7.cg.tilePrimaryBadge': 'Ensisijainen',
  'r7.cg.tileUnlinkAria': (area: string) => `Poista liitos: ${area}`,
  'r7.cg.tileUnlinked': 'Liitos poistettu.',
  'r7.cg.tileLinkTitle': 'Liitä osa-alue',
  'r7.cg.tileAreaPick': 'Osa-alue',
  'r7.cg.tilePrimaryCheck': 'Ensisijainen osa-alue — laatta ammentaa kysymyspoolinsa siitä',
  'r7.cg.tileLink': 'Liitä',
  'r7.cg.tileLinked': 'Liitetty.',

  'r7.cg.scaleListTitle': 'Vastausasteikkomallit',
  'r7.cg.scaleHint':
    'Kolmen vaihtoehdon vastausasteikot, joita vasten kysymys kirjoitetaan (spec §14.9) — matala, keski, korkea, kullakin oma emoji ja teksti.',
  'r7.cg.scaleNewTitle': 'Lisää asteikkomalli',
  'r7.cg.scaleCode': 'Koodi',
  'r7.cg.scaleCodeHint': 'Pienet kirjaimet, numerot ja alaviivat, esim. frequency.',
  'r7.cg.scaleOptionLow': 'Matala vaihtoehto',
  'r7.cg.scaleOptionMid': 'Keskimmäinen vaihtoehto',
  'r7.cg.scaleOptionHigh': 'Korkea vaihtoehto',
  'r7.cg.scaleEmoji': 'Emoji',
  'r7.cg.scaleOptionLabel': 'Teksti',
  'r7.cg.scaleAdd': 'Lisää asteikko',
  'r7.cg.scaleAdded': 'Lisätty.',
  'r7.cg.scaleEmpty': 'Ei vastausasteikkoja vielä.',
  'r7.cg.scaleRetire': 'Poista käytöstä',
  'r7.cg.scaleRetireTitle': 'Poistetaanko tämä asteikko käytöstä?',
  'r7.cg.scaleRetireBody': 'Sille ei voi enää kirjoittaa uutta kysymystä. Sitä jo käyttävät kysymykset toimivat edelleen ennallaan.',
  'r7.cg.scaleRetiredToast': 'Poistettu käytöstä.',

  'r7.cg.qListTitle': 'Alustan kysymyspankki',
  'r7.cg.qHint':
    'Zenceran omat jatkokysymykset, automaattisesti hyväksyttyjä ja näkyvissä kaikille kouluille, paitsi jos koulu on kirjoittanut omansa samalle osa-alueelle.',
  'r7.cg.qNewTitle': 'Lisää alustan kysymys',
  'r7.cg.qFilterLabel': 'Osa-alue',
  'r7.cg.qFilterAll': 'Kaikki osa-alueet',
  'r7.cg.qLens': 'Vaihe',
  'r7.cg.qLensThink': 'Ajattele',
  'r7.cg.qLensAct': 'Toimi',
  'r7.cg.qLensReflect': 'Pohdi',
  'r7.cg.qAxis': 'Akseli',
  'r7.cg.qAxisThriving': 'Kukoistus',
  'r7.cg.qAxisStrain': 'Kuormitus',
  'r7.cg.qAdd': 'Lisää kysymys',
  'r7.cg.qAdded': 'Lisätty.',
  'r7.cg.qEmpty': 'Ei alustan kysymyksiä vielä tälle osa-alueelle.',

  'err.bad_code': 'Käytä pieniä kirjaimia, numeroita ja alaviivoja, alkaen kirjaimesta.',
  'err.duplicate': 'Tämä koodi on jo käytössä.',
  'err.bad_lens': 'Valitse Ajattele, Toimi tai Pohdi.',
  'err.bad_axis': 'Valitse Kukoistus tai Kuormitus.',
  'err.unknown_tile': 'Tuntematon tai poistettu laatta.',
  'err.unknown_link': 'Tätä laattaa ei ole liitetty tähän osa-alueeseen.',
  'err.last_area': 'Laatalla on oltava vähintään yksi liitetty osa-alue.',
  'err.bad_options': 'Anna kaikille kolmelle vaihtoehdolle emoji ja teksti.',
};

export const r7Sv: R7Strings = {
  'r7.cg.nav': 'Innehåll',
  'r7.cg.title': 'Plattformens innehåll',
  'r7.cg.hint':
    'De plattformsstyrda byggstenarna bakom elevens incheckning: de sex välmåendeområdena, rutorna eleven trycker på, svarsskalorna frågor skrivs mot, och Zenceras egen frågebank.',
  'r7.cg.tabAreas': 'Områden',
  'r7.cg.tabTiles': 'Rutor',
  'r7.cg.tabScales': 'Svarsskalor',
  'r7.cg.tabQuestions': 'Plattformsfrågor',

  'r7.cg.statusActive': 'Aktiv',
  'r7.cg.statusRetired': 'Avaktiverad',

  'r7.cg.areaListTitle': 'Välmåendeområden',
  'r7.cg.areaHint':
    'De sex SWIPE-forskningsområdena varje uppföljningsfråga poängsätts mot (spec §14.1). Att lägga till ett gäller bara framtida incheckningar; att avaktivera behåller historiken.',
  'r7.cg.areaNewTitle': 'Lägg till ett område',
  'r7.cg.areaCode': 'Kod',
  'r7.cg.areaCodeHint': 'Gemener, siffror och understreck, t.ex. feel.',
  'r7.cg.areaSort': 'Sorteringsordning',
  'r7.cg.areaSortHint': 'Lägre nummer visas först. Lämna tomt för att lägga till sist.',
  'r7.cg.areaAdd': 'Lägg till område',
  'r7.cg.areaAdded': 'Tillagt.',
  'r7.cg.areaEmpty': 'Inga områden ännu.',
  'r7.cg.areaRetire': 'Avaktivera',
  'r7.cg.areaRetireTitle': 'Avaktivera detta område?',
  'r7.cg.areaRetireBody':
    'Ingen ny fråga kan riktas mot det, och det försvinner från framtida incheckningar. Tidigare svar behåller sin betydelse — avaktivering tar aldrig bort dem.',
  'r7.cg.areaRetiredToast': 'Avaktiverat.',

  'r7.cg.tileListTitle': 'Elevens rutor',
  'r7.cg.tileHint':
    'Det eleven faktiskt trycker på i incheckningen (spec §14.2) — ett lager ovanpå de sex områdena. En ruta kan länka till mer än ett område; ett tryck krediterar varje länkat område fullt ut, och ett länkat område är markerat som primärt — det rutan hämtar sina uppföljningsfrågor från.',
  'r7.cg.tileNewTitle': 'Lägg till en ruta',
  'r7.cg.tileCode': 'Kod',
  'r7.cg.tileCodeHint': 'Gemener, siffror och understreck, t.ex. friends.',
  'r7.cg.tileSort': 'Sorteringsordning',
  'r7.cg.tileAdd': 'Lägg till ruta',
  'r7.cg.tileAdded': 'Tillagd.',
  'r7.cg.tileEmpty': 'Inga rutor ännu.',
  'r7.cg.tileRetire': 'Avaktivera',
  'r7.cg.tileRetireTitle': 'Avaktivera denna ruta?',
  'r7.cg.tileRetireBody': 'Eleverna slutar se den i incheckningen. Tidigare svar behåller sin betydelse — avaktivering tar aldrig bort dem.',
  'r7.cg.tileRetiredToast': 'Avaktiverad.',
  'r7.cg.tileAreasLabel': 'Länkade områden',
  'r7.cg.tilePrimaryBadge': 'Primärt',
  'r7.cg.tileUnlinkAria': (area: string) => `Ta bort länk: ${area}`,
  'r7.cg.tileUnlinked': 'Länk borttagen.',
  'r7.cg.tileLinkTitle': 'Länka ett område',
  'r7.cg.tileAreaPick': 'Område',
  'r7.cg.tilePrimaryCheck': 'Primärt område — rutan hämtar sin frågepool från det',
  'r7.cg.tileLink': 'Länka',
  'r7.cg.tileLinked': 'Länkad.',

  'r7.cg.scaleListTitle': 'Svarsskalemallar',
  'r7.cg.scaleHint':
    'De 3-delade svarsskalorna en fråga skrivs mot (spec §14.9) — låg, mellan, hög, var och en med egen emoji och text.',
  'r7.cg.scaleNewTitle': 'Lägg till en skalmall',
  'r7.cg.scaleCode': 'Kod',
  'r7.cg.scaleCodeHint': 'Gemener, siffror och understreck, t.ex. frequency.',
  'r7.cg.scaleOptionLow': 'Lågt alternativ',
  'r7.cg.scaleOptionMid': 'Mellanalternativ',
  'r7.cg.scaleOptionHigh': 'Högt alternativ',
  'r7.cg.scaleEmoji': 'Emoji',
  'r7.cg.scaleOptionLabel': 'Text',
  'r7.cg.scaleAdd': 'Lägg till skala',
  'r7.cg.scaleAdded': 'Tillagd.',
  'r7.cg.scaleEmpty': 'Inga svarsskalor ännu.',
  'r7.cg.scaleRetire': 'Avaktivera',
  'r7.cg.scaleRetireTitle': 'Avaktivera denna skala?',
  'r7.cg.scaleRetireBody': 'Ingen ny fråga kan skrivas mot den. Frågor som redan använder den fortsätter fungera som förut.',
  'r7.cg.scaleRetiredToast': 'Avaktiverad.',

  'r7.cg.qListTitle': 'Plattformens frågebank',
  'r7.cg.qHint':
    'Zencera-skrivna uppföljningsfrågor, automatiskt godkända och visade för alla skolor om inte skolan har skrivit sin egen för samma område.',
  'r7.cg.qNewTitle': 'Lägg till en plattformsfråga',
  'r7.cg.qFilterLabel': 'Område',
  'r7.cg.qFilterAll': 'Alla områden',
  'r7.cg.qLens': 'Lins',
  'r7.cg.qLensThink': 'Tänk',
  'r7.cg.qLensAct': 'Agera',
  'r7.cg.qLensReflect': 'Reflektera',
  'r7.cg.qAxis': 'Axel',
  'r7.cg.qAxisThriving': 'Blomstrande',
  'r7.cg.qAxisStrain': 'Belastning',
  'r7.cg.qAdd': 'Lägg till fråga',
  'r7.cg.qAdded': 'Tillagd.',
  'r7.cg.qEmpty': 'Inga plattformsfrågor ännu för det här området.',

  'err.bad_code': 'Använd gemener, siffror och understreck, med en bokstav först.',
  'err.duplicate': 'Den koden används redan.',
  'err.bad_lens': 'Välj Tänk, Agera eller Reflektera.',
  'err.bad_axis': 'Välj Blomstrande eller Belastning.',
  'err.unknown_tile': 'Okänd eller avaktiverad ruta.',
  'err.unknown_link': 'Den rutan är inte länkad till det området.',
  'err.last_area': 'En ruta behöver minst ett länkat område.',
  'err.bad_options': 'Ge alla tre alternativ en emoji och en text.',
};
